import React, { useContext, useMemo } from 'react';
import ConnectedFormContext from '../../components/Context/ConnectedFormContext';
import { useDispatch } from 'react-redux';
import { default as Form } from '../../components/connectedForm';
import * as input from '../../components/connectedForm/inputComponents';
import { ICustomer, IPhysicalDimensionDescription, IProduct } from '../../graphql/schema/p4types';
import { IBlurTextFieldProps } from '../../components/connectedForm/BlurTextField/BlurTextField';
import { IDropdownProps } from '../../@types/dropdown';
import { IDropdownOptionRenderer } from '../../@types/entityTypes';

const PhysicalDimensionOptionRenderer: IDropdownOptionRenderer = ({
  option,
}: {
  option: IPhysicalDimensionDescription;
}) => {
  return (
    <span>
      {option.description} - {option.lengthUnitOfMeasure!.toLowerCase()}
    </span>
  );
};

export const VolumeFields = () => {
  const connectedForm = useContext(ConnectedFormContext);
  const dispatch = useDispatch();

  const values = connectedForm.values as IProduct;
  const dimensions = values.packagingDimensions;
  const lengthUnitOfMeasure = useMemo(
    () => dimensions?.physicalDimensionDescription?.lengthUnitOfMeasure?.toLowerCase(),
    [dimensions?.physicalDimensionDescription?.lengthUnitOfMeasure]
  );

  const calculateVolume = ({
    length = dimensions?.length,
    width = dimensions?.width,
    depth = dimensions?.depth,
    units = lengthUnitOfMeasure,
  }) => {
    let divide: number = 1;

    if (units === 'mm') {
      divide = 1e3;
    } else if (units === 'cm') {
      divide = 1e2;
    }

    if (length && width && depth) {
      const volume = (Number(length / divide) * Number(width / divide) * Number(depth / divide)).toFixed(9);
      const fixedVolume = volume.replace(/(\.[0-9]*[1-9])0+$|\.0*$/, '$1');

      dispatch(connectedForm.change('packagingDimensions.volume', fixedVolume));
    }
  };

  return (
    <>
      <Form.Row>
        <Form.Fieldset cols={1}>
          <Form.Legend label="Packaging" />
        </Form.Fieldset>
        <Form.Fieldset cols={1 / 3}>
          <Form.Field<IDropdownProps>
            dataid="packagingDimensions.physicalDimensionDescription"
            component={input.Dropdown}
            optionRenderer={PhysicalDimensionOptionRenderer}
            label="Packaging Description"
            labelCols={2 / 5}
            inputCols={3 / 5}
            onChange={(ev, value) => {
              calculateVolume({ units: value?.lengthUnitOfMeasure.toLowerCase() });
            }}
          />
          <Form.Field<IBlurTextFieldProps>
            dataid="packagingDimensions.weight"
            component={input.BlurTextField}
            label="Weight"
            labelCols={2 / 5}
            inputCols={3 / 5}
          />
        </Form.Fieldset>
      </Form.Row>
      <Form.Row>
        <Form.Fieldset cols={1 / 3}>
          <Form.Field<IBlurTextFieldProps>
            dataid="packagingDimensions.length"
            component={input.BlurTextField}
            enableOnChange={true}
            infoMessage={lengthUnitOfMeasure}
            label="Length"
            labelCols={2 / 5}
            inputCols={3 / 5}
            onChange={(event, newValue) => {
              calculateVolume({ length: newValue });
            }}
          />
        </Form.Fieldset>
        <Form.Fieldset cols={1 / 3}>
          <Form.Field<IBlurTextFieldProps>
            dataid="packagingDimensions.width"
            component={input.BlurTextField}
            enableOnChange={true}
            infoMessage={lengthUnitOfMeasure}
            label="Width"
            labelCols={2 / 5}
            inputCols={3 / 5}
            onChange={(event, newValue) => {
              calculateVolume({ width: newValue });
            }}
          />
        </Form.Fieldset>
        <Form.Fieldset cols={1 / 3}>
          <Form.Field<IBlurTextFieldProps>
            dataid="packagingDimensions.depth"
            component={input.BlurTextField}
            enableOnChange={true}
            infoMessage={lengthUnitOfMeasure}
            label="Depth"
            labelCols={2 / 5}
            inputCols={3 / 5}
            onChange={(event, newValue) => {
              calculateVolume({ depth: newValue });
            }}
          />
        </Form.Fieldset>
        <Form.Fieldset cols={1 / 3}>
          <Form.Field<IBlurTextFieldProps>
            dataid="packagingDimensions.volume"
            component={input.BlurTextField}
            infoMessage="m³"
            label="Volume"
            labelCols={2 / 5}
            inputCols={3 / 5}
          />
        </Form.Fieldset>
      </Form.Row>
    </>
  );
};
